<template>
  <div class="w-64 flex-shrink-0 flex items-center flex-col relative">
    <span
      v-if="qrcreate.imageData"
      class="absolute -top-6 text-grow text-sm left-0 flex items-center gap-1 cursor-pointer"
      @click="qrcreate.modal_embed = true"
    >
      <CodeIcon size="18" stroke-width="1" />
      Embed
    </span>
    <span
      v-if="qrcreate.imageData"
      class="absolute -top-6 text-grow text-sm right-0 flex items-center gap-1 cursor-pointer"
      @click="resetStyle"
    >
      Reset
      <RefreshDotIcon size="16" stroke-width="1" />
    </span>
    <template v-if="!qrcreate.imageData">
      <div
        class="w-full min-h-[258px] bg-white text-black flex items-center justify-center mb-6"
      >
        <span>{{ $t("preview_qrcode") }}</span>
      </div>
      <div class="flex items-center justify-center">
        <div
          @click="qrcreate.modal_style = true"
          class="px-6 border-gray-200 border-r flex items-center gap-2 cursor-pointer hover:text-success text-black"
        >
          <WandIcon stroke-width="1" size="20" />
          <span class="whitespace-nowrap">{{ $t("editor") }}</span>
        </div>
        <nuxt-link
          :to="localePath('/qr-code-scanner')"
          class="px-6 flex items-center gap-2 cursor-pointer hover:text-success text-black"
        >
          <ScanIcon stroke-width="1" size="20" />
          <span class="whitespace-nowrap">
            {{ $t("decoder") }}
          </span>
        </nuxt-link>
      </div>
    </template>
    <template v-else>
      <div
        class="border bg-white border-gray-200 p-3 flex flex-col justify-center items-center"
      >
        <div class="p-4 border border-gray-200 shadow-sm rounded-md mb-4">
          <img :src="qrcreate.imageData" class="w-full h-full" alt="" />
        </div>
        <div
          class="mb-2 flex items-center justify-center gap-2 text-gray-500 hover:text-green-500 cursor-pointer"
          @click="OpenConfigModal()"
        >
          <div class="font-normal text-xs font-inter flex-shrink-0 flex">
            Error correction: {{ levelError }},
            <template v-if="qrcreate.download.format == 'svg'">
              <span
                class="ml-2 rounded-sm font-medium bg-[#ff5500] leading-5 px-1.5 text-white"
              >
                <SvgIcon size="16" />
              </span>
            </template>
            <template v-else>{{ size }}</template>
          </div>
          <SettingsIcon stroke-width="1" size="16" />
        </div>
        <div class="mb-2 flex items-center justify-center w-full gap-2">
          <button
            @click="OpenConfigLogo()"
            class="text-sm w-full h-8 flex justify-center items-center border border-gray-200 text-black font-light hover:text-success hover:border-success"
          >
            Upload Logo
          </button>
          <button
            @click="OepnConfigStyle()"
            class="text-sm w-full h-8 flex justify-center items-center border border-gray-200 text-black font-light hover:text-success hover:border-success"
          >
            Edit Style
          </button>
        </div>
        <div id="svg-display" ref="svgDom" class="hidden"></div>
        <button
          :disabled="loading"
          class="w-full disabled:opacity-60 mb-4 h-[38px] bg-success flex items-center justify-center text-white hover:bg-success-tier"
          @click="downloadImage()"
        >
          <span class="mr-1" v-if="loading">
            <IconsLoadingDecorator class="w-4" /> </span
          >Download
        </button>
      </div>
    </template>
  </div>
  <ClientOnly>
    <QrcreateConfigQrDialog />
    <QrcreateStyleQrDialog />
    <QrcreateEmbedQRDialog />
  </ClientOnly>
</template>
<script lang="ts" setup>
import {
  ScanIcon,
  WandIcon,
  SettingsIcon,
  RefreshDotIcon,
  SvgIcon,
  CodeIcon,
} from "vue-tabler-icons";
import { useQrCreateStore } from "~~/stores/qrcreate";
const qrcreate = useQrCreateStore();
const qrstyle = useQrStyle();
const localePath = useLocalePath();
const svgDom = ref<HTMLElement>();
const loading = ref(false);
const levelError = computed(() => {
  switch (qrcreate.config.qrOptions.errorCorrectionLevel) {
    case "L":
      return "7%";
    case "M":
      return "15%";
    case "H":
      return "30%";
  }
  return "25%";
});
const size = computed(() => {
  return qrcreate.download.width + "x" + qrcreate.download.height;
});
const OpenConfigModal = () => {
  qrcreate.modal_config = true;
};
const OpenConfigLogo = () => {
  qrcreate.tab_active = "LOGO";
  qrcreate.modal_style = true;
};
const OepnConfigStyle = () => {
  if (qrcreate.select_template.id) {
    qrcreate.tab_active = "Edit Style";
  } else {
    qrcreate.tab_active = "QR Code";
  }
  qrcreate.modal_style = true;
};

const setCookie = (name: any, value: any, daysToExpire: any) => {
  const date = new Date();
  date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
  const expires = "expires=" + date.toUTCString();
  document.cookie = name + "=" + value + "; " + expires + "; path=/";
};
const getCookie = (name: any) => {
  const cookieArray = document.cookie.split("; ");
  for (let i = 0; i < cookieArray.length; i++) {
    const cookie = cookieArray[i].split("=");
    if (cookie[0] === name) {
      return cookie[1];
    }
  }
  return null;
};
const downloadImage = async () => {
  loading.value = true;
  if (typeof document !== "undefined") {
    if (!getCookie("first_download_modal")) {
      qrcreate.first_download_modal = true;
      setCookie("first_download_modal", 1, 0.5);
    }
  }
  qrstyle
    .PrepareDownload()
    .then(async (options) => {
      if (options.is_template) {
        var svg = svgDom.value?.querySelector("svg");
        var svgData = svg?.outerHTML ?? "";

        var newImg = new Image();
        var canva = document.createElement("canvas");
        var ctx = canva.getContext("2d");
        var svgBlob = new Blob([svgData], {
          type: "image/svg+xml;charset=utf-8",
        });
        var svgUrl = URL.createObjectURL(svgBlob);
        if (qrcreate.download.format == "svg") {
          downloadByUrl(
            svgUrl,
            generateRandomString(16) + "_qrgateway." + qrcreate.download.format
          );
          loading.value = false;
          return;
        }
        newImg.src = svgUrl;
        newImg.onload = () => {
          canva.width = qrcreate.download.width;
          canva.height = qrcreate.download.height;
          ctx?.drawImage(
            newImg,
            0,
            0,
            qrcreate.download.width,
            qrcreate.download.height
          );
          var dataURL = canva.toDataURL("image/" + qrcreate.download.format);

          downloadByUrl(
            dataURL,
            generateRandomString(16) + "_qrgateway." + qrcreate.download.format
          );
          loading.value = false;
        };
        newImg.onerror = function (err) {
          loading.value = false;
          return err;
        };
      } else {
        var base64DL = options.imageData;
        if (qrcreate.download.format == "svg") {
          //@ts-ignore
          var qrcanvas = new QRCodeStyling({ ...qrcreate.config });
          var blob = await qrcanvas.getRawData("svg");
          base64DL = await useUtils().readBlobToBase64(blob);
        }
        downloadByUrl(
          base64DL,
          generateRandomString(16) + "_qrgateway." + qrcreate.download.format
        );
        loading.value = false;
      }
    })
    .catch((err) => {
      loading.value = false;
    });
};
const downloadByUrl = (url: string, filename: string) => {
  var downloadLink = document.createElement("a");
  downloadLink.href = url;
  downloadLink.download = filename;
  downloadLink.click();
  downloadLink.remove();
};
const generateRandomString = (length: number) => {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};
const resetStyle = async () => {
  qrcreate.config = {
    width: 300,
    height: 300,
    data: qrcreate.config.data,
    margin: 0,
    qrOptions: {
      typeNumber: "0",
      mode: "Byte",
      errorCorrectionLevel: "Q",
    },
    dotsOptions: {
      type: "square",
      color: "#000000",
    },
    image: "",
    imageOptions: {
      hideBackgroundDots: true,
      imageSize: 0.4,
      margin: 0,
      rounded: 0,
    },
    cornersSquareOptions: { type: "square" },
    cornersDotOptions: { type: "" },
    backgroundOptions: {
      color: "transparent",
    },
    corner_custom_color: false,
    image_default: false,
    background_default: false,
  };
  qrcreate.select_template = {};
  qrcreate.fields = {};
  await qrstyle.GenerateQrCode({ stack: false });
};
onMounted(() => {
  nextTick(() => {
    if (typeof localStorage !== "undefined") {
      var downloadConfig = localStorage.getItem("__download_format");
      if (downloadConfig) {
        //@ts-ignore
        qrcreate.download = JSON.parse(downloadConfig);
      }
    }
  });
});
</script>
